// NotificationsCenter — مركز تنبيهات موحّد (3.4.10): نقص مخزون / صلاحية / محاسبة.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { hasActionPermission } from '../../utils/accessControl';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const sevColor = { error: C.rose, warning: '#b8860b', info: C.appbar };

export default function NotificationsCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // الفئات المسموح بها تأتي من الخادم (حسب صلاحية الفئة لكل دور). null = لم تُحمَّل بعد.
  const [allowedCats, setAllowedCats] = useState([]);
  const [active, setActive] = useState(null); // الفئات المُختارة للعرض (null = الكل المسموح)
  const canManageSettings = hasActionPermission('notifications-manage');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // المدد من الإعدادات الدائمة؛ والفلترة بالصلاحية تتمّ في الخادم. نمرّر المُختار فقط.
      const params = active && active.length ? { categories: active.join(',') } : undefined;
      const res = await api.get('/notifications', params ? { params } : undefined);
      setData(res.data);
      setAllowedCats(res.data.allowedCategories || []);
      setActive((cur) => (cur === null ? (res.data.allowedCategories || []) : cur));
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setLoading(false); }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const catLabel = useMemo(() => ({
    low_stock: t('low_stock', 'نقص المخزون'),
    expiring: t('expiring', 'قرب الصلاحية'),
    farrier_due: t('farrier_due', 'البيطار'),
    reproduction_due: t('reproduction_due', 'عيادة الإنتاج'),
    accounting: t('accounting', 'المحاسبة'),
  }), [t]);

  const toggle = (cat) => setActive((cur) => {
    const base = cur || allowedCats;
    return base.includes(cat) ? base.filter((c) => c !== cat) : [...base, cat];
  });

  return (
    <SmartPage>
      <SmartHeader title={t('notifications', 'التنبيهات')} subtitle={t('notifications_hint', 'تنبيهات حيّة من الوحدات في مكان واحد — قابلة للتصفية.')} />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {!loading && allowedCats.length === 0 ? (
        <Alert severity="info">{t('no_notification_categories', 'لا فئات تنبيهات ضمن صلاحياتك.')}</Alert>
      ) : (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
          {allowedCats.map((cat) => (
            <Chip key={cat} label={catLabel[cat]} clickable color={(active || allowedCats).includes(cat) ? 'primary' : 'default'}
              variant={(active || allowedCats).includes(cat) ? 'filled' : 'outlined'} onClick={() => toggle(cat)} />
          ))}
          <Box sx={{ flex: 1 }} />
          {canManageSettings ? (
            <Button startIcon={<TuneIcon />} onClick={() => navigate('/settings/notifications')} variant="text">{t('settings', 'الإعدادات')}</Button>
          ) : null}
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} variant="outlined">{t('refresh', 'تحديث')}</Button>
        </Stack>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : data ? (
        data.items.length === 0 ? (
          <Alert severity="success">{t('no_notifications', 'لا تنبيهات — كل شيء على ما يرام.')} ✅</Alert>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: C.muted, mb: 1 }}>{data.total} {t('alerts', 'تنبيهًا')}</Typography>
            {data.items.map((n, i) => (
              <Box key={i} onClick={() => n.link && navigate(n.link)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                  border: `1px solid ${C.line}`, borderLeft: `4px solid ${sevColor[n.severity] || C.line}`,
                  borderRadius: 1.5, cursor: n.link ? 'pointer' : 'default', bgcolor: C.card,
                  '&:hover': n.link ? { bgcolor: C.cardSoft } : undefined,
                }}>
                <Chip size="small" label={catLabel[n.category] || n.category} sx={{ bgcolor: C.cardSoft, fontWeight: 800 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, color: C.text }}>{n.title}</Typography>
                  <Typography variant="caption" sx={{ color: C.muted }}>{n.detail}</Typography>
                </Box>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: sevColor[n.severity] || C.line }} />
              </Box>
            ))}
          </Stack>
        )
      ) : null}
    </SmartPage>
  );
}
